"use client";

import {
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type ReactNode,
  type RefObject,
} from "react";
import { cx } from "../cx.ts";
import { Icon, type IconName } from "./icon.tsx";
import { IconButton } from "./icon-button.tsx";

export type MenuItem =
  | {
      kind: "item";
      key: string;
      label: string;
      icon?: IconName;
      onSelect: () => void;
      tone?: "default" | "danger";
      disabled?: boolean;
    }
  | { kind: "separator"; key: string };

export type DropdownMenuTriggerProps = {
  ref: RefObject<HTMLButtonElement | null>;
  id: string;
  "aria-haspopup": "menu";
  "aria-expanded": boolean;
  onClick: () => void;
  onKeyDown: (event: React.KeyboardEvent) => void;
};

/**
 * A menu, hand-rolled, because there is no Radix here and will not be.
 *
 * **The panel is `position: fixed`, and that is not a style choice.** A row
 * action lives inside `DataTable`, whose shell is `overflow-hidden` (it clips
 * the header and footer to the card's radius) and whose scroller is
 * `overflow-x-auto`. An absolutely-positioned panel is clipped by both, and on
 * the last row it would be clipped to nothing. A fixed panel is laid out
 * against the viewport and escapes both.
 *
 * The condition that would silently undo this: a fixed element positions
 * against the nearest ancestor carrying `transform`, `filter`, `perspective`,
 * `backdrop-filter`, `will-change` or `contain: paint`. No ancestor has one
 * today. Adding one to the shell or the table would misplace every menu in the
 * product, and no test would notice.
 *
 * The menu closes on scroll rather than tracking the trigger: a panel that
 * drifts away from the control that opened it is worse than one that dismisses.
 */
export function DropdownMenu({
  items,
  label,
  icon = "more-horizontal",
  align = "end",
  size = "md",
  trigger,
}: {
  items: readonly MenuItem[];
  /** Names the trigger for a screen reader. App callers pass `t(...)`. */
  label: string;
  icon?: IconName;
  align?: "start" | "end";
  size?: "sm" | "md";
  /** Supply a custom trigger; it must spread every prop it is handed. */
  trigger?: (props: DropdownMenuTriggerProps) => ReactNode;
}) {
  const [open, setOpen] = useState(false);
  const [position, setPosition] = useState<{ top: number; left: number } | null>(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const menuRef = useRef<HTMLDivElement | null>(null);
  const itemRefs = useRef<(HTMLButtonElement | null)[]>([]);
  // `useId`, not a random string: this renders on the server too, and a
  // value drawn from Math.random() differs between the two passes and trips a
  // hydration mismatch.
  const triggerId = useId();

  const selectable = items.flatMap((item, index) =>
    item.kind === "item" && !item.disabled ? [index] : [],
  );

  const close = useCallback((restoreFocus: boolean) => {
    setOpen(false);
    setPosition(null);
    if (restoreFocus) triggerRef.current?.focus();
  }, []);

  function openWith(index: number) {
    setActiveIndex(index);
    setOpen(true);
  }

  // Measured after paint, because placement depends on the panel's own height:
  // it flips above the trigger when there is no room below.
  useLayoutEffect(() => {
    if (!open) return;
    const triggerEl = triggerRef.current;
    const menuEl = menuRef.current;
    if (!triggerEl || !menuEl) return;

    const rect = triggerEl.getBoundingClientRect();
    const { width, height } = menuEl.getBoundingClientRect();
    const below = rect.bottom + 4;
    const top = below + height > window.innerHeight - 8 ? rect.top - height - 4 : below;
    const preferred = align === "end" ? rect.right - width : rect.left;
    const left = Math.min(Math.max(8, preferred), window.innerWidth - width - 8);
    setPosition({ top: Math.max(8, top), left });
  }, [open, align, items.length]);

  useEffect(() => {
    if (!open || position === null) return;
    itemRefs.current[activeIndex]?.focus();
  }, [open, position, activeIndex]);

  useEffect(() => {
    if (!open) return;
    function onPointerDown(event: PointerEvent) {
      const target = event.target as Node;
      if (menuRef.current?.contains(target) || triggerRef.current?.contains(target)) return;
      close(false);
    }
    function onDismiss() {
      close(false);
    }
    // `capture` so the table's own horizontal scroller is heard too, not just
    // the window.
    document.addEventListener("pointerdown", onPointerDown);
    window.addEventListener("scroll", onDismiss, true);
    window.addEventListener("resize", onDismiss);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown);
      window.removeEventListener("scroll", onDismiss, true);
      window.removeEventListener("resize", onDismiss);
    };
  }, [open, close]);

  function step(delta: number) {
    if (selectable.length === 0) return;
    const at = selectable.indexOf(activeIndex);
    const next = (at + delta + selectable.length) % selectable.length;
    setActiveIndex(selectable[next]!);
  }

  function onTriggerKeyDown(event: React.KeyboardEvent) {
    if (event.key === "ArrowDown" || event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      openWith(selectable[0] ?? 0);
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      openWith(selectable[selectable.length - 1] ?? 0);
    }
  }

  function onMenuKeyDown(event: React.KeyboardEvent) {
    switch (event.key) {
      case "ArrowDown":
        event.preventDefault();
        step(1);
        break;
      case "ArrowUp":
        event.preventDefault();
        step(-1);
        break;
      case "Home":
        event.preventDefault();
        setActiveIndex(selectable[0] ?? 0);
        break;
      case "End":
        event.preventDefault();
        setActiveIndex(selectable[selectable.length - 1] ?? 0);
        break;
      case "Escape":
        event.preventDefault();
        close(true);
        break;
      case "Tab":
        close(false);
        break;
      default:
        break;
    }
  }

  const triggerProps: DropdownMenuTriggerProps = {
    ref: triggerRef,
    id: triggerId,
    "aria-haspopup": "menu",
    "aria-expanded": open,
    onClick: () => (open ? close(false) : openWith(selectable[0] ?? 0)),
    onKeyDown: onTriggerKeyDown,
  };

  return (
    <>
      {trigger ? (
        // `triggerProps` carries the ref object for the caller to attach, which
        // is what a ref is for. Nothing here reads `.current`; the four reads
        // are all in effects and handlers above. The rule flags the call
        // because it cannot see into `trigger`.
        // oxlint-disable-next-line react/refs
        trigger(triggerProps)
      ) : (
        <IconButton icon={icon} label={label} size={size} {...triggerProps} />
      )}
      {open ? (
        <div
          ref={menuRef}
          role="menu"
          aria-labelledby={triggerId}
          onKeyDown={onMenuKeyDown}
          style={{
            top: position?.top ?? 0,
            left: position?.left ?? 0,
            visibility: position ? "visible" : "hidden",
          }}
          className="fixed z-50 min-w-52 rounded-card border border-line bg-surface-raised py-1 shadow-popover"
        >
          {items.map((item, index) =>
            item.kind === "separator" ? (
              <div key={item.key} role="separator" className="my-1 h-px bg-line-subtle" />
            ) : (
              <button
                key={item.key}
                type="button"
                role="menuitem"
                tabIndex={-1}
                disabled={item.disabled}
                ref={(node) => {
                  itemRefs.current[index] = node;
                }}
                onClick={() => {
                  // Close first, so focus is back on the trigger before the
                  // action runs. A drawer opened by this item then calls
                  // `showModal()` afterwards and keeps the focus it takes;
                  // in the other order the menu would snatch it straight back.
                  close(true);
                  item.onSelect();
                }}
                className={cx(
                  "flex w-full items-center gap-2.5 px-3 py-2 text-start text-sm",
                  "transition-colors duration-150 focus:outline-none",
                  "disabled:cursor-not-allowed disabled:opacity-50",
                  item.tone === "danger"
                    ? "text-danger hover:bg-danger-soft focus:bg-danger-soft"
                    : "text-ink-body hover:bg-surface-subtle focus:bg-surface-subtle",
                )}
              >
                {item.icon ? <Icon name={item.icon} size={16} /> : null}
                {item.label}
              </button>
            ),
          )}
        </div>
      ) : null}
    </>
  );
}
